import React, { useRef } from 'react';
import { Repeat } from 'lucide-react';
import { useFocusTrap } from '../../../hooks/useFocusTrap';

interface EditRecurringDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onEditSingle: () => void;
  onEditSeries: () => void;
  eventTitle: string;
}

export default function EditRecurringDialog({
  isOpen,
  onClose,
  onEditSingle,
  onEditSeries,
  eventTitle,
}: EditRecurringDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, isOpen);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[70] p-4 animate-fadeInBackdrop">
      <div ref={dialogRef} role="dialog" aria-modal="true" className="bg-[var(--modal-bg)] rounded-xl w-full max-w-sm p-6 shadow-xl animate-fadeIn">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-[var(--accent-subtle)] rounded-lg">
            <Repeat className="w-5 h-5 text-[var(--accent-primary)]" />
          </div>
          <h3 className="text-base font-semibold text-[var(--content-primary)]">Edit Recurring Event</h3>
        </div>

        <p className="text-sm text-[var(--content-secondary)] mb-5">
          "{eventTitle}" repeats. Do you want to edit only this occurrence, or all events in the series?
        </p>

        <div className="space-y-2">
          <button
            onClick={onEditSingle}
            className="w-full px-4 py-3 text-left border border-[var(--border-primary)] rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors"
          >
            <div className="font-medium text-sm text-[var(--content-primary)]">This event only</div>
            <div className="text-xs text-[var(--content-tertiary)] mt-0.5">Only this occurrence will be changed</div>
          </button>

          <button
            onClick={onEditSeries}
            className="w-full px-4 py-3 text-left border border-[var(--border-primary)] rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors"
          >
            <div className="font-medium text-sm text-[var(--content-primary)]">All events in series</div>
            <div className="text-xs text-[var(--content-tertiary)] mt-0.5">Every occurrence will be updated</div>
          </button>
        </div>

        <button
          onClick={onClose}
          className="w-full mt-3 px-4 py-2 text-sm bg-[var(--surface-tertiary)] text-[var(--content-secondary)] rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
